import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ObscuredData } from '@se/components';
import { CpExamination } from '../../../models/custom-page';
import { IVisitWorklistSearchingParams, VisitExamination } from '../../../models/visit';
import { SignType } from 'estensa-module';
import { Observable } from 'rxjs';
import {
    Addendum,
    AddExamOptions,
    ExaminationsFilter,
    HardCodedDataUpdate,
    IResponseStatus,
    UnlockData,
} from '../../report-workflow-exam.service';
import {
    EcgChartState,
    IScrybaCertificate,
    ISeSignResultData,
    SeConfirmoSignInfo,
    SeMultiSignInfo,
    SeScrybaSignDespatchOtpInfo,
    SeSignInfo,
    SissData,
} from '../../../models/estensasign.model';
import { ServerRootService } from '../../../utils/routing/serverroot.service';
import { SearchFormType } from '../../../search-page/search-page-type.enum';
import { AuthData, SissUploadDocumentData } from '@se/models';

export abstract class VisitBaseService extends ServerRootService {
    protected readonly httpClient = inject(HttpClient);

    protected approveBase(
        reportIds: string[],
        authData: AuthData | null,
        otherCredentials: string[],
        isDeferred = false,
        isFromWorklist?: boolean,
        displayedSettings?: EcgChartState,
        referringPhysicianId?: string,
    ) {
        return this.httpClient.post<IResponseStatus[]>(
            this.getServerRoot() + '/Visit/Approve',
            { reportIds, isDeferred, isFromWorklist, displayedSettings, otherCredentials, referringPhysicianId },
            this.buildOptions(authData),
        );
    }
    protected undoMultiValidationBase(
        authData: AuthData,
        reportId: string,
        otherCredentials: string[],
        isFromWorklist?: boolean,
    ) {
        return this.httpClient.post<IResponseStatus>(
            this.getServerRoot() + '/Visit/UndoApprove',
            { reportId, isFromWorklist, otherCredentials },
            this.buildOptions(authData),
        );
    }

    undoValidation(reportId: string, authData: AuthData, isFromWorklist?: boolean) {
        return this.httpClient.post(
            this.getServerRoot() + '/Visit/UndoApprove',
            { reportId, isFromWorklist },
            this.buildOptions(authData),
        );
    }

    deleteExaminations(ids: string[]) {
        return this.httpClient.put(this.getServerRoot() + '/Visit/DeleteExaminations', ids, this.buildOptions());
    }

    forceUnlockMultiple(data: UnlockData[]) {
        return this.httpClient.put(this.getServerRoot() + '/Visit/ForceUnlockMultiple', data, this.buildOptions());
    }

    unlock(data: UnlockData[]) {
        return this.httpClient.put(this.getServerRoot() + '/Visit/Unlock', data, this.buildOptions());
    }

    getDataForSissSign(examinationIds: any) {
        return this.httpClient.get<SissData>(
            this.getServerRoot() + '/ECG/GetSissData/?examinationId=' + examinationIds,
            this.buildOptions(),
        );
    }

    uploadReportToSissCloud(data: SissUploadDocumentData) {
        return this.httpClient.post<ISeSignResultData[]>(
            `${this.getServerRoot()}/Visit/UploadReportToSissCloud`,
            {
                type: SignType.SISSCloud,
                sissCloud: {
                    reportIdList: data.reportIds,
                    signerId: data.signerId,
                    signerUsername: data.signerUsername,
                    token: data.token,
                },
                isDeferred: data.isDeferred,
            },
            this.buildOptions(),
        );
    }

    uploadReportToSissWayCloud(data: SissUploadDocumentData) {
        return this.httpClient.post<ISeSignResultData[]>(
            `${this.getServerRoot()}/Visit/UploadReportToSissWayCloud`,
            {
                type: SignType.SISSWayCloud,
                sissWayCloud: {
                    reportIdList: data.reportIds,
                    signerId: data.signerId,
                    signerUsername: data.signerUsername,
                    token: data.token,
                    reportDocumentiList: data.reportDocumentiList,
                    idStruttura: data.idStruttura,
                    idSubStruttura: data.idSubStruttura,
                    codBrancaSpecialistica: data.codBrancaSpecialistica,
                },
                isDeferred: data.isDeferred,
            },
            this.buildOptions(),
        );
    }

    getPdf(info: SeSignInfo) {
        return this.httpClient.post(this.getServerRoot() + '/Visit/GetPdfVisitReport', info, this.buildOptions());
    }

    getVisitPdf(reportIds: string[], displayedSettings: any, smCertificateCode?: string) {
        return this.httpClient.post<string>(
            this.getServerRoot() + '/Visit/GetPdf',
            { reportIds, displayedSettings, smCertificateCode },
            this.buildOptions(),
        );
    }

    getReportExaminations(idReport: string): Observable<CpExamination[]> {
        return this.httpClient.post<VisitExamination[]>(
            this.getServerRoot() + '/Visit/GetReportExaminations',
            { idReport },
            this.buildOptions(),
        );
    }

    lockExams(examinations: VisitExamination[]) {
        return this.httpClient.post(this.getServerRoot() + '/Visit/LockExams', examinations, this.buildOptions());
    }

    merge(formType: SearchFormType, source: string, target: string) {
        return this.httpClient.post(
            this.getServerRoot() + '/' + formType + '/MergeReport',
            {
                IdExaminationSource: source,
                IdExaminationTarget: target,
            },
            this.buildOptions(),
        );
    }

    undoMerge(formType: SearchFormType, examinationId: string) {
        return this.httpClient.post(
            this.getServerRoot() + '/' + formType + '/UndoMergeReport',
            JSON.stringify(examinationId),
            this.buildOptions(),
        );
    }

    sign(info: SeSignInfo) {
        return this.httpClient.post<ISeSignResultData[]>(
            this.getServerRoot() + '/Visit/SignReport',
            info,
            this.buildOptions(),
        );
    }

    /*83782*/
    multiSign(info: SeMultiSignInfo) {
        return this.httpClient.post<ISeSignResultData[]>(
            this.getServerRoot() + '/Visit/MultiSignReport',
            info,
            this.buildOptions(),
        );
    }

    saveAddendum(data: Addendum) {
        return this.httpClient.post<Addendum>(this.getServerRoot() + '/Visit/SaveAddendum', data, this.buildOptions());
    }

    deleteAddendum(data: Addendum) {
        return this.httpClient.post<Addendum>(
            this.getServerRoot() + '/Visit/DeleteAddendum',
            data,
            this.buildOptions(),
        );
    }

    saveHardCodedData(update: HardCodedDataUpdate) {
        return this.httpClient.post<ObscuredData>(
            this.getServerRoot() + '/Visit/SaveHardCodedData',
            update,
            this.buildOptions(),
        );
    }

    searchExaminationsToAddToReport(options: ExaminationsFilter) {
        return this.httpClient.post<CpExamination[]>(
            this.getServerRoot() + '/Visit/GetExaminationsToAdd',
            options,
            this.buildOptions(),
        );
    }

    searchExaminationsToRemoveFromReport(idReport: string) {
        return this.getReportExaminations(idReport);
    }

    addExaminationsToReport(options: AddExamOptions) {
        return this.httpClient.post(
            this.getServerRoot() + '/Visit/AddExaminationsToReport',
            options,
            this.buildOptions(),
        );
    }

    removeExaminationsFromReport(idReport: string, idExaminations: string[]) {
        return this.httpClient.post(
            this.getServerRoot() + '/Visit/RemoveExaminationsToReport',
            { idReport, idExaminations },
            this.buildOptions(),
        );
    }

    getVisitWorklist(searchParams: IVisitWorklistSearchingParams) {
        return this.httpClient.post(
            this.getServerRoot() + '/Visit/GetVisitWorklist',
            searchParams,
            this.buildOptions(),
        );
    }

    getScrybaCertificate(signerId: string): Observable<IScrybaCertificate[]> {
        return this.httpClient.post<IScrybaCertificate[]>(
            this.getServerRoot() + '/Visit/GetScrybaCertificates',
            '"' + signerId + '"',
            this.buildOptions(),
        );
    }

    getConfirmoSignConfiguration(signerId: string): Observable<SeConfirmoSignInfo> {
        return this.httpClient.post<SeConfirmoSignInfo>(
            this.getServerRoot() + '/Visit/GetConfirmoSignConfiguration',
            '"' + signerId + '"',
            this.buildOptions(),
        );
    }

    GetConfirmoOtp(confirmoInfo: SeConfirmoSignInfo): Observable<string> {
        return this.httpClient.post<string>(
            this.getServerRoot() + '/Visit/GetConfirmoOtp',
            confirmoInfo,
            this.buildOptions(),
        );
    }

    //signerId: string, pin: string, otpType: string, certId:string
    createDespatchOtpRequest(otpInfo: SeScrybaSignDespatchOtpInfo): Observable<string> {
        return this.httpClient.post<string>(
            this.getServerRoot() + '/Visit/CreateDespatchOtpRequest',
            otpInfo,
            this.buildOptions(),
        );
    }

    getExaminationById(examinationId: string): Observable<VisitExamination> {
        return this.httpClient.post<VisitExamination>(
            this.getServerRoot() + '/Visit/GetExaminationById',
            '"' + examinationId + '"',
            this.buildOptions(),
        );
    };

}
